import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Mic as MicIcon,
  Person as PersonIcon,
  AssignmentInd as AssignMeIcon,
  Favorite as HeartIcon,
  Scale as WeightIcon,
  Height as HeightIcon,
  Thermostat as TempIcon,
  AccessTime as TimeIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Description as ReportIcon,
  NoteAdd as AddReportIcon,
  CalendarToday as CalendarIcon,
  ShowChart as ShowChartIcon,
  MonitorHeart as MonitorHeartIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';

import SharedPatients from '../components/SharedPatients';
import ProviderQueueControl from '../components/ProviderQueueControl';
import { useStartSession } from '../components/StartSessionGate';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import PatientVitalsChart from '../components/PatientVitalsChart';
import VitalsSummary from '../components/VitalsSummary';
import ReportUpload, { reportKindLabel } from '../components/ReportUpload';
import { openReportFile } from './Reports';
import { formatTemp } from '../utils/temperature';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** @returns {{ omit: true } | { invalid: true } | { value: number }} */
function optionalIntField(raw) {
  if (raw === null || raw === undefined) return { omit: true };
  const t = String(raw).trim();
  if (t === '') return { omit: true };
  const n = parseInt(t, 10);
  return Number.isNaN(n) ? { invalid: true } : { value: n };
}

/** @returns {{ omit: true } | { invalid: true } | { value: number }} */
function optionalFloatField(raw) {
  if (raw === null || raw === undefined) return { omit: true };
  const t = String(raw).trim();
  if (t === '') return { omit: true };
  const n = parseFloat(t);
  return Number.isNaN(n) ? { invalid: true } : { value: n };
}

/**
 * Prepare an API patient payload for the editable form. The API returns
 * `temperature` as a display string (e.g. "98.6°F" or "N/A"); the edit field
 * must hold the raw numeric value, otherwise saving sends "N/A"/"98.6°F" to the
 * backend, which rejects it (422) and the whole vitals update fails to persist.
 */
function toEditablePatient(p) {
  if (!p) return p;
  return { ...p, temperature: p.temperature_value ?? '' };
}

const fmtVisitDay = (iso) => new Date(`${iso}T00:00:00`)
  .toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

/** "Last visit: 12 Oct 2026" — and, on History, when this doctor last saw them. */
function lastVisitLine(p, isProvider, scope) {
  if (scope === 'history' && isProvider && p.my_last_visit) {
    const extra = p.last_visit && p.last_visit !== p.my_last_visit ? ` · last visit ${fmtVisitDay(p.last_visit)}` : '';
    return `You last saw them ${fmtVisitDay(p.my_last_visit)}${extra} · ${p.visit_count} visit${p.visit_count === 1 ? '' : 's'}`;
  }
  if (!p.last_visit) return 'No visits yet';
  return `Last visit: ${fmtVisitDay(p.last_visit)}${p.visit_count > 1 ? ` · ${p.visit_count} visits` : ''}`;
}

const reportDay = (r) => r.report_date || r.visit_date || (r.created_at || '').slice(0, 10);
const shortDay = (iso) => (iso
  ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
  : '—');

/** One report as a button that opens the file. */
function ReportLink({ r }) {
  return (
    <Chip
      icon={<ReportIcon fontSize="small" />}
      label={`${r.title} · ${reportKindLabel(r)}`}
      size="small" variant="outlined" clickable
      onClick={() => openReportFile(`${API_URL}/api/v1/reports/${r.id}/file`)}
    />
  );
}

/** Visits and reports in one list, newest first. A report linked to a visit sits on
 *  that visit's card; one that isn't is an entry of its own, on its report date. */
function buildTimeline(visits, reports) {
  const visitIds = new Set(visits.map((v) => v.visit_id));
  const byVisit = {};
  reports.forEach((r) => {
    if (r.session_id && visitIds.has(r.session_id)) (byVisit[r.session_id] = byVisit[r.session_id] || []).push(r);
  });
  const items = [
    ...visits.map((v) => ({ kind: 'visit', key: `v-${v.visit_id}`, day: v.date || '', visit: v, reports: byVisit[v.visit_id] || [] })),
    ...reports.filter((r) => !(r.session_id && visitIds.has(r.session_id)))
      .map((r) => ({ kind: 'report', key: `r-${r.id}`, day: reportDay(r), report: r })),
  ];
  return items.sort((a, b) => (b.day || '').localeCompare(a.day || ''));
}

export default function PatientList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { assignmentVersion } = useNotifications();
  const isStaff = user?.role === 'staff';
  const isClinicAdmin = user?.role === 'clinic_admin';
  const isProvider = user?.role === 'provider' || user?.role === 'clinic_admin';
  const canAssignProvider = isStaff || isClinicAdmin;
  const [patients, setPatients] = useState([]);
  // 'all' | 'mine' — the same clinic roster asked about two ways, not two levels of
  // access. Everyone at the clinic can open any of its patients.
  const [scope, setScope] = useState('all');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [editedPatient, setEditedPatient] = useState(null);
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [visitHistory, setVisitHistory] = useState([]);
  const [patientReports, setPatientReports] = useState([]);
  const [addingReport, setAddingReport] = useState(false);
  const [vitalsReadings, setVitalsReadings] = useState([]);
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recordVitalsOpen, setRecordVitalsOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignProviderId, setAssignProviderId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [savingVital, setSavingVital] = useState(false);
  const [clinicians, setClinicians] = useState([]);
  const [cliniciansLoaded, setCliniciansLoaded] = useState(false);
  const [readingRecord, setReadingRecord] = useState({
    date: '',
    time: '',
    bp_systolic: '',
    bp_diastolic: '',
    weight_kg: '',
    height_cm: '',
    temperature: '',
    pulse: '',
    respiratory_rate: '',
  });

  useEffect(() => {
    fetchPatients();
  }, [assignmentVersion]);

  useEffect(() => {
    const { openPatientId, openTab } = location.state || {};
    if (openPatientId && patients.length > 0) {
      const patient = patients.find((p) => p.patient_id === openPatientId);
      if (patient) {
        let initialTab = 0;
        if (!isStaff) {
          if (openTab === 2 && isProvider) initialTab = 2;
          else if (openTab === 1) initialTab = 1;
        }
        handlePatientClick(patient, initialTab);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlePatientClick omitted to avoid re-open loops
  }, [location.state, patients, isStaff, isProvider]);

  // History: patients already seen — by this doctor if a provider, by anyone at the
  // clinic otherwise — most recent first.
  const seenOn = useCallback((p) => (isProvider ? p.my_last_visit : p.last_visit), [isProvider]);

  useEffect(() => {
    let list = patients;
    if (scope === 'history') {
      list = patients.filter((p) => seenOn(p))
        .sort((a, b) => (seenOn(b) || '').localeCompare(seenOn(a) || ''));
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.reason?.toLowerCase().includes(q));
    }
    setFilteredPatients(list);
  }, [searchTerm, patients, scope, seenOn]);

  useEffect(() => {
    if (scope === 'shared') return;      // SharedPatients loads its own list
    setLoading(true);
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/clinical/patients${scope === 'mine' ? '?mine=true' : ''}`);
      setPatients(response.data.patients);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchVitalsTrends = async (patientId) => {
    setVitalsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/clinical/patients/${patientId}/vitals-history?months=6`);
      setVitalsReadings(res.data.readings || []);
    } catch {
      setVitalsReadings([]);
    } finally {
      setVitalsLoading(false);
    }
  };

  const openRecordVitalsPanel = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    setReadingRecord({
      date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
      bp_systolic: '',
      bp_diastolic: '',
      weight_kg: '',
      height_cm: '',
      temperature: '',
      pulse: '',
      respiratory_rate: '',
    });
    setRecordVitalsOpen(true);
  };

  const closePatientDialog = () => {
    setOpenDialog(false);
    setRecordVitalsOpen(false);
    setIsEditingPatient(false);
  };

  // Take an unassigned patient onto your own list. Only a patient's assigned doctor may
  // start a visit, so this is what turns "View Details" into "Start Session".
  const [assigningId, setAssigningId] = useState(null);
  const assignToMe = async (patient) => {
    setAssigningId(patient.patient_id);
    try {
      await axios.post(`${API_URL}/api/v1/clinical/patients/${patient.patient_id}/assign-to-me`);
      toast.success(`${patient.name} is now on your list — you can start a session`);
      const mark = (list) => list.map((x) => (x.patient_id === patient.patient_id
        ? { ...x, is_mine: true, is_unassigned: false } : x));
      setPatients(mark); setFilteredPatients(mark);
      setSelectedPatient((sel) => (sel && sel.patient_id === patient.patient_id
        ? { ...sel, is_mine: true, is_unassigned: false } : sel));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not assign this patient to you');
    } finally {
      setAssigningId(null);
    }
  };

  const handlePatientClick = async (patient, initialTab = 0) => {
    setSelectedPatient(patient);
    setIsEditingPatient(false);
    setRecordVitalsOpen(false);
    setVitalsReadings([]);
    const tab = isStaff ? 0 : isProvider ? Math.min(Math.max(0, initialTab), 2) : 0;
    setDialogTab(tab);
    try {
      const response = await axios.get(`${API_URL}/api/v1/clinical/patients/${patient.patient_id}`);
      setPatientDetails(response.data.patient);
      setEditedPatient(toEditablePatient(response.data.patient));
      setOpenDialog(true);
      if (isStaff) {
        setVisitHistory([]);
        setPatientReports([]);
      } else {
        fetchVisitHistory(patient.patient_id);
      }
    } catch (error) {
      toast.error('Failed to load patient details');
    }
  };

  useEffect(() => {
    if (!openDialog || !selectedPatient || !isProvider || dialogTab !== 2) return;
    fetchVitalsTrends(selectedPatient.patient_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchVitalsTrends is stable
  }, [openDialog, selectedPatient, isProvider, dialogTab]);

  const openAssignDialog = async () => {
    setAssignProviderId(patientDetails?.provider_id || '');
    setAssignOpen(true);
    if (cliniciansLoaded) return;
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/assignable-clinicians`);
      setClinicians(res.data?.clinicians || []);
      setCliniciansLoaded(true);
    } catch {
      toast.error('Failed to load clinicians');
    }
  };

  const handleAssignSubmit = async () => {
    if (!patientDetails?.patient_id || !assignProviderId) return;
    setAssigning(true);
    try {
      await axios.put(
        `${API_URL}/api/v1/admin/patients/${patientDetails.patient_id}/assign-provider`,
        { provider_id: assignProviderId },
      );
      const refreshed = await axios.get(
        `${API_URL}/api/v1/clinical/patients/${patientDetails.patient_id}`,
      );
      setPatientDetails(refreshed.data.patient);
      setEditedPatient(toEditablePatient(refreshed.data.patient));
      setAssignOpen(false);
      toast.success('Doctor assigned');
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const fetchVisitHistory = async (patientId) => {
    // Visits and reports come from two endpoints; one failing must not hide the other.
    const [visits, reports] = await Promise.allSettled([
      axios.get(`${API_URL}/api/v1/clinical/patients/${patientId}/history`),
      axios.get(`${API_URL}/api/v1/reports`, { params: { patient_id: patientId } }),
    ]);
    setVisitHistory(visits.status === 'fulfilled' ? visits.value.data.visits || [] : []);
    setPatientReports(reports.status === 'fulfilled' ? reports.value.data.reports || [] : []);
  };

  const handleSavePatientEdits = async () => {
    if (!editedPatient?.patient_id || savingEdits) return;

    if (isStaff) {
      setSavingEdits(true);
      try {
        const payload = {};
        if (editedPatient.full_name != null) payload.full_name = editedPatient.full_name;
        if (editedPatient.date_of_birth != null) payload.date_of_birth = editedPatient.date_of_birth;
        if (editedPatient.reason != null) payload.reason = editedPatient.reason;
        const response = await axios.put(
          `${API_URL}/api/v1/clinical/patients/${editedPatient.patient_id}`,
          payload
        );
        setPatientDetails(response.data.patient);
        setEditedPatient(toEditablePatient(response.data.patient));
        setIsEditingPatient(false);
        fetchPatients();
        toast.success('Patient details updated');
      } catch (error) {
        toast.error('Failed to save patient details');
      } finally {
        setSavingEdits(false);
      }
      return;
    }

    const systolic = optionalIntField(editedPatient.bp_systolic);
    const diastolic = optionalIntField(editedPatient.bp_diastolic);
    const weightKg = optionalFloatField(editedPatient.weight_kg);
    const heightCm = optionalFloatField(editedPatient.height_cm);
    const pulse = optionalIntField(editedPatient.pulse);
    const respiratory = optionalIntField(editedPatient.respiratory_rate);
    const numericChecks = [
      ['Systolic BP', systolic],
      ['Diastolic BP', diastolic],
      ['Weight (kg)', weightKg],
      ['Height (cm)', heightCm],
      ['Pulse', pulse],
      ['Respiratory rate', respiratory]
    ];
    for (const [label, parsed] of numericChecks) {
      if (parsed.invalid) {
        toast.error(`${label}: enter a valid number or leave the field empty.`);
        return;
      }
    }
    setSavingEdits(true);
    try {
      const payload = {};
      if (editedPatient.full_name != null) payload.full_name = editedPatient.full_name;
      if (editedPatient.date_of_birth != null) payload.date_of_birth = editedPatient.date_of_birth;
      if (!systolic.omit) payload.bp_systolic = systolic.value;
      if (!diastolic.omit) payload.bp_diastolic = diastolic.value;
      if (!weightKg.omit) payload.weight_kg = weightKg.value;
      if (!heightCm.omit) payload.height_cm = heightCm.value;
      if (editedPatient.temperature != null && String(editedPatient.temperature).trim() !== '') {
        payload.temperature = String(editedPatient.temperature).trim();
      }
      if (!pulse.omit) payload.pulse = pulse.value;
      if (!respiratory.omit) payload.respiratory_rate = respiratory.value;
      if (editedPatient.reason != null) payload.reason = editedPatient.reason;
      const response = await axios.put(`${API_URL}/api/v1/clinical/patients/${editedPatient.patient_id}`, payload);
      setPatientDetails(response.data.patient);
      setEditedPatient(toEditablePatient(response.data.patient));
      setIsEditingPatient(false);
      fetchPatients();
      if (selectedPatient?.patient_id) {
        fetchVitalsTrends(selectedPatient.patient_id);
      }
      toast.success('Patient details updated');
    } catch (error) {
      toast.error('Failed to save patient details');
    } finally {
      setSavingEdits(false);
    }
  };

  const handleSaveVitalReading = async () => {
    if (!editedPatient?.patient_id || savingVital) return;
    const { date, time } = readingRecord;
    if (!date || !time || String(time).trim() === '') {
      toast.error('Date and time are required for when the vitals were taken.');
      return;
    }
    const timeStr = String(time).trim();
    const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    const recorded = new Date(`${date}T${normalizedTime}`);
    if (Number.isNaN(recorded.getTime())) {
      toast.error('Invalid date or time.');
      return;
    }
    const systolic = optionalIntField(readingRecord.bp_systolic);
    const diastolic = optionalIntField(readingRecord.bp_diastolic);
    const weightKg = optionalFloatField(readingRecord.weight_kg);
    const heightCm = optionalFloatField(readingRecord.height_cm);
    const pulse = optionalIntField(readingRecord.pulse);
    const respiratory = optionalIntField(readingRecord.respiratory_rate);
    const numericChecks = [
      ['Systolic BP', systolic],
      ['Diastolic BP', diastolic],
      ['Weight (kg)', weightKg],
      ['Height (cm)', heightCm],
      ['Pulse', pulse],
      ['Respiratory rate', respiratory],
    ];
    for (const [label, parsed] of numericChecks) {
      if (parsed.invalid) {
        toast.error(`${label}: enter a valid number or leave the field empty.`);
        return;
      }
    }
    const hasAnyVital =
      !systolic.omit ||
      !diastolic.omit ||
      !weightKg.omit ||
      !heightCm.omit ||
      !pulse.omit ||
      !respiratory.omit ||
      (readingRecord.temperature != null && String(readingRecord.temperature).trim() !== '');
    if (!hasAnyVital) {
      toast.error('Enter at least one vital (BP, weight, temperature, etc.).');
      return;
    }
    const payload = { recorded_at: recorded.toISOString() };
    if (!systolic.omit) payload.bp_systolic = systolic.value;
    if (!diastolic.omit) payload.bp_diastolic = diastolic.value;
    if (!weightKg.omit) payload.weight_kg = weightKg.value;
    if (!heightCm.omit) payload.height_cm = heightCm.value;
    if (readingRecord.temperature != null && String(readingRecord.temperature).trim() !== '') {
      payload.temperature = String(readingRecord.temperature).trim();
    }
    if (!pulse.omit) payload.pulse = pulse.value;
    if (!respiratory.omit) payload.respiratory_rate = respiratory.value;
    setSavingVital(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/clinical/patients/${editedPatient.patient_id}/vital-readings`,
        payload
      );
      setPatientDetails(response.data.patient);
      setEditedPatient(toEditablePatient(response.data.patient));
      setRecordVitalsOpen(false);
      fetchPatients();
      toast.success('Vital reading saved');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save vital reading');
    } finally {
      setSavingVital(false);
    }
  };

  // Checks how many AI sessions are left before opening the recorder.
  const { startSession, gateDialog } = useStartSession();
  const handleStartSession = () => {
    if (selectedPatient) {
      setOpenDialog(false);
      startSession(selectedPatient.patient_id);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Patients
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {isProvider ? 'Select a patient to start a scribe session' : 'View and manage patient information'}
      </Typography>

      {/* The same "Your queue" card as the dashboard: who's with you, who's next, call next. */}
      {isProvider && <ProviderQueueControl />}

      <Tabs
        value={scope}
        onChange={(_, v) => setScope(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="all" label="All patients" />
        <Tab value="mine" label="My patients" />
        {/* Patients already seen, newest visit first. */}
        <Tab value="history" label="History" />
        {/* Patients the organisation's other offices share with this one — read-only. */}
        <Tab value="shared" label="Shared with us" />
      </Tabs>

      {scope === 'shared' ? <SharedPatients canSeeHistory={!isStaff} /> : (<>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search patients by name or reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : filteredPatients.length === 0 ? (
          <Grid item xs={12}>
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {searchTerm
                ? 'No patients match that search.'
                : scope === 'history'
                  ? (isProvider ? "You haven't seen any patients yet. Once a visit's note is written, the patient appears here." : 'No patients have been seen yet.')
                  : 'No patients yet.'}
            </Typography>
          </Grid>
        ) : (
          filteredPatients.map((patient) => (
          <Grid item xs={12} md={6} lg={4} key={patient.patient_id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => handlePatientClick(patient)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <PersonIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {patient.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      DOB: {patient.dob}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {lastVisitLine(patient, isProvider, scope)}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={patient.reason}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                {/* Only the patient's assigned doctor starts a session for them; everyone
                    else at the clinic can still open the record. */}
                {isProvider && patient.is_mine ? (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<MicIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePatientClick(patient);
                    }}
                  >
                    Start Session
                  </Button>
                ) : isProvider && patient.is_unassigned ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AssignMeIcon />}
                    disabled={assigningId === patient.patient_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      assignToMe(patient);
                    }}
                  >
                    {assigningId === patient.patient_id ? 'Assigning…' : 'Assign to me'}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePatientClick(patient);
                    }}
                  >
                    View Details
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))
        )}
      </Grid>
      </>)}

      {gateDialog}

      <Dialog open={openDialog} onClose={closePatientDialog} maxWidth={dialogTab === 2 && isProvider ? 'lg' : 'md'} fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            {patientDetails?.full_name || 'Patient Details'}
          </Box>
          {patientDetails && dialogTab === 0 && isStaff && !isEditingPatient && !recordVitalsOpen && (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button startIcon={<EditIcon />} size="small" variant="outlined" onClick={() => setIsEditingPatient(true)}>
                Edit
              </Button>
              <Button startIcon={<MonitorHeartIcon />} size="small" variant="contained" onClick={openRecordVitalsPanel}>
                Record vitals
              </Button>
            </Box>
          )}
          {patientDetails && dialogTab === 0 && isStaff && isEditingPatient && (
            <Button startIcon={savingEdits ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              size="small" variant="contained" disabled={savingEdits} onClick={handleSavePatientEdits}>
              {savingEdits ? 'Saving…' : 'Save'}
            </Button>
          )}
          {patientDetails && dialogTab === 0 && !isStaff && !isEditingPatient && (
            <Button startIcon={<EditIcon />} size="small" onClick={() => setIsEditingPatient(true)} sx={{ flexShrink: 0 }}>
              Edit
            </Button>
          )}
          {patientDetails && dialogTab === 0 && !isStaff && isEditingPatient && (
            <Button startIcon={savingEdits ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              size="small" disabled={savingEdits} onClick={handleSavePatientEdits} sx={{ flexShrink: 0 }}>
              {savingEdits ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
          {patientDetails && editedPatient && (
            <Box sx={{ mt: 1 }}>
              {isProvider && (
                <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ mb: 2 }}>
                  <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label="Details" />
                  <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
                  <Tab icon={<ShowChartIcon fontSize="small" />} iconPosition="start" label="Vitals trends" />
                </Tabs>
              )}

              {dialogTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64 }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  {isEditingPatient ? (
                    <>
                      <TextField
                        fullWidth
                        label="Full Name"
                        size="small"
                        value={editedPatient.full_name || ''}
                        onChange={(e) => setEditedPatient({ ...editedPatient, full_name: e.target.value })}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        size="small"
                        type="date"
                        value={editedPatient.date_of_birth || ''}
                        onChange={(e) => setEditedPatient({ ...editedPatient, date_of_birth: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                        sx={{ mb: isStaff ? 1 : 0 }}
                      />
                      {isStaff && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {editedPatient.email}
                        </Typography>
                      )}
                      {isStaff && (
                        <TextField
                          fullWidth
                          label="Visit reason"
                          size="small"
                          value={editedPatient.reason ?? ''}
                          onChange={(e) => setEditedPatient({ ...editedPatient, reason: e.target.value })}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" fontWeight={600}>
                        {patientDetails.full_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {patientDetails.email} • DOB: {patientDetails.date_of_birth}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              {!isEditingPatient && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    border: '1px solid rgba(4, 47, 46, 0.08)',
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                      ASSIGNED DOCTOR
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                      {/* The name already carries the title the provider chose ("Dr Helen
                          Whitfield"); adding one here read as "Dr. Dr Helen Whitfield". */}
                      {patientDetails.provider_name || 'Not assigned'}
                    </Typography>
                  </Box>
                  {canAssignProvider && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={openAssignDialog}
                    >
                      {patientDetails.provider_id ? 'Reassign' : 'Assign'}
                    </Button>
                  )}
                </Box>
              )}

              {!(isStaff && isEditingPatient) && (
              <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Latest Vitals
              </Typography>
              {!isEditingPatient && patientDetails?.last_vitals_recorded_at && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Last recorded:{' '}
                  {new Date(patientDetails.last_vitals_recorded_at).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
              )}
              {isEditingPatient && isStaff ? null : isEditingPatient ? (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="BP Systolic"
                      value={editedPatient.bp_systolic ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, bp_systolic: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="BP Diastolic"
                      value={editedPatient.bp_diastolic ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, bp_diastolic: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Weight (kg)"
                      value={editedPatient.weight_kg ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, weight_kg: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Height (cm)"
                      value={editedPatient.height_cm ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, height_cm: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Temperature (°C or °F)"
                      value={editedPatient.temperature ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, temperature: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pulse (bpm)"
                      value={editedPatient.pulse ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, pulse: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Respiratory Rate"
                      value={editedPatient.respiratory_rate ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, respiratory_rate: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Visit Reason"
                      value={editedPatient.reason ?? ''}
                      onChange={(e) => setEditedPatient({ ...editedPatient, reason: e.target.value })}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  <ListItem>
                    <HeartIcon color="primary" sx={{ mr: 2 }} />
                    <ListItemText
                      primary="Blood Pressure"
                      secondary={
                        patientDetails.bp_systolic != null && patientDetails.bp_diastolic != null
                          ? `${patientDetails.bp_systolic}/${patientDetails.bp_diastolic} mmHg`
                          : '—'
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <WeightIcon color="primary" sx={{ mr: 2 }} />
                    <ListItemText
                      primary="Weight"
                      secondary={patientDetails.weight_kg != null ? `${patientDetails.weight_kg} kg` : '—'}
                    />
                  </ListItem>
                  <ListItem>
                    <HeightIcon color="primary" sx={{ mr: 2 }} />
                    <ListItemText
                      primary="Height"
                      secondary={patientDetails.height_cm != null ? `${patientDetails.height_cm} cm` : '—'}
                    />
                  </ListItem>
                  <ListItem>
                    <TempIcon color="primary" sx={{ mr: 2 }} />
                    <ListItemText
                      primary="Temperature"
                      secondary={
                        patientDetails.temperature_value != null
                          ? formatTemp(patientDetails.temperature_value)
                          : '—'
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TimeIcon color="primary" sx={{ mr: 2 }} />
                    <ListItemText
                      primary="Pulse / RR"
                      secondary={
                        patientDetails.pulse != null || patientDetails.respiratory_rate != null
                          ? `${patientDetails.pulse ?? '—'} bpm / ${patientDetails.respiratory_rate ?? '—'} rpm`
                          : '—'
                      }
                    />
                  </ListItem>
                </List>
              )}
              </>
              )}

              {isStaff && recordVitalsOpen && (
                <Collapse in={recordVitalsOpen}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MonitorHeartIcon fontSize="small" color="primary" />
                    New vital reading
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {
                      "Enter when this measurement was taken, then add vitals. Each save adds another point in the patient's history (visible to providers on the trends chart)."
                    }
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="date"
                        label="Date taken"
                        InputLabelProps={{ shrink: true }}
                        value={readingRecord.date}
                        onChange={(e) => setReadingRecord({ ...readingRecord, date: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="time"
                        label="Time taken"
                        InputLabelProps={{ shrink: true }}
                        value={readingRecord.time}
                        onChange={(e) => setReadingRecord({ ...readingRecord, time: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="BP Systolic"
                        value={readingRecord.bp_systolic}
                        onChange={(e) => setReadingRecord({ ...readingRecord, bp_systolic: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="BP Diastolic"
                        value={readingRecord.bp_diastolic}
                        onChange={(e) => setReadingRecord({ ...readingRecord, bp_diastolic: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Weight (kg)"
                        value={readingRecord.weight_kg}
                        onChange={(e) => setReadingRecord({ ...readingRecord, weight_kg: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Height (cm)"
                        value={readingRecord.height_cm}
                        onChange={(e) => setReadingRecord({ ...readingRecord, height_cm: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Temperature (°C or °F)"
                        value={readingRecord.temperature}
                        onChange={(e) => setReadingRecord({ ...readingRecord, temperature: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Pulse (bpm)"
                        value={readingRecord.pulse}
                        onChange={(e) => setReadingRecord({ ...readingRecord, pulse: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Respiratory rate"
                        value={readingRecord.respiratory_rate}
                        onChange={(e) => setReadingRecord({ ...readingRecord, respiratory_rate: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" disabled={savingVital} onClick={() => setRecordVitalsOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained"
                      startIcon={savingVital ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      disabled={savingVital} onClick={handleSaveVitalReading}>
                      {savingVital ? 'Saving…' : 'Save reading'}
                    </Button>
                  </Box>
                </Collapse>
              )}

              {!isEditingPatient && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Visit Reason
                  </Typography>
                  <Chip label={patientDetails.reason || '—'} color="primary" />
                </>
              )}
            </Box>
              )}

              {isProvider && dialogTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Visits and reports
                </Typography>
                <Button size="small" startIcon={<AddReportIcon />} onClick={() => setAddingReport(true)}>
                  Add report
                </Button>
              </Box>
              {visitHistory.length === 0 && patientReports.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No visits or reports yet. Record a session, or add a blood test, scan or letter, and it will appear here.
                </Typography>
              ) : (
                <List dense>
                  {buildTimeline(visitHistory, patientReports).map((item) => {
                    if (item.kind === 'report') {
                      const r = item.report;
                      return (
                        <Card key={item.key} variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <ReportIcon fontSize="small" color="action" />
                              <Typography variant="subtitle2" fontWeight={600}>{shortDay(item.day)}</Typography>
                              <Chip label={reportKindLabel(r)} size="small" variant="outlined" />
                            </Box>
                            <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Added by {r.uploaded_by_role === 'patient' ? 'the patient' : (r.uploaded_by_name || 'the clinic')}
                            </Typography>
                            {r.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{r.notes}</Typography>}
                            <Button size="small" sx={{ mt: 1 }} onClick={() => openReportFile(`${API_URL}/api/v1/reports/${r.id}/file`)}>
                              Open report
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    }
                    const visit = item.visit;
                    return (
<Card key={item.key} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            {shortDay(visit.date)}
                          </Typography>
                          <Chip label={visit.reason} size="small" variant="outlined" />
                          {visit.status === 'review' && (
                            <Chip label="Pending approval" size="small" color="warning" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {visit.doctor_name}
                        </Typography>
                        {visit.summary && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {visit.summary}
                          </Typography>
                        )}
                        {visit.icd10_codes?.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                            {visit.icd10_codes.slice(0, 3).map((c, i) => (
                              <Chip key={i} label={c.code} size="small" variant="outlined" />
                            ))}
                          </Box>
                        )}
                        {item.reports.length > 0 && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                              REPORTS FROM THIS VISIT
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                              {item.reports.map((r) => <ReportLink key={r.id} r={r} />)}
                            </Box>
                          </>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                          VITALS AT THIS VISIT
                        </Typography>
                        <VitalsSummary
                          vitals={visit.vitals}
                          sx={{ mt: 1 }}
                          emptyText="No vitals were recorded for this visit."
                        />
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() => navigate(`/review/${visit.visit_id}`)}
                        >
                          View Note
                        </Button>
                      </CardContent>
                    </Card>
                    );
                  })}
                </List>
              )}
              {selectedPatient && (
                <ReportUpload
                  open={addingReport}
                  onClose={() => setAddingReport(false)}
                  onSaved={() => { setAddingReport(false); fetchVisitHistory(selectedPatient.patient_id); }}
                  patient={{ id: selectedPatient.patient_id, name: patientDetails?.full_name || '' }}
                />
              )}
            </Box>
              )}

              {isProvider && dialogTab === 2 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Vitals over time
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Each intake save and each approved visit adds a point. Compare trends across multiple visits per month.
                  </Typography>
                  {vitalsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <PatientVitalsChart readings={vitalsReadings} />
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePatientDialog}>Close</Button>
          {isProvider && selectedPatient?.is_mine && (
            <Button variant="contained" startIcon={<MicIcon />} onClick={handleStartSession}>
              Start Session
            </Button>
          )}
          {isProvider && selectedPatient && !selectedPatient.is_mine && selectedPatient.is_unassigned && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                No doctor yet — assign them to yourself to start a session.
              </Typography>
              <Button variant="contained" startIcon={<AssignMeIcon />}
                disabled={assigningId === selectedPatient.patient_id}
                onClick={() => assignToMe(selectedPatient)}>
                {assigningId === selectedPatient.patient_id ? 'Assigning…' : 'Assign to me'}
              </Button>
            </>
          )}
          {isProvider && selectedPatient && !selectedPatient.is_mine && !selectedPatient.is_unassigned && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              {patientDetails?.provider_name
                ? `Assigned to ${patientDetails.provider_name} — only they can start a session. Ask the front desk to reassign.`
                : 'Only their assigned doctor can start a session.'}
            </Typography>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {patientDetails?.provider_id ? 'Reassign Doctor' : 'Assign Doctor'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {patientDetails?.full_name
              ? `Select a doctor for ${patientDetails.full_name}.`
              : 'Select a doctor for this patient.'}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="assign-provider-label">Doctor</InputLabel>
            <Select
              labelId="assign-provider-label"
              label="Doctor"
              value={assignProviderId}
              onChange={(e) => setAssignProviderId(e.target.value)}
            >
              {clinicians.length === 0 && (
                <MenuItem value="" disabled>
                  No clinicians available
                </MenuItem>
              )}
              {clinicians.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.full_name} {c.role === 'clinic_admin' ? '(Clinic admin)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignSubmit}
            disabled={
              assigning ||
              !assignProviderId ||
              assignProviderId === patientDetails?.provider_id
            }
          >
            {assigning ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
